import type { ReactNode } from "react";
import { Icon, type IconName } from "@/components/ui/Icon/Icon";
import { MediaPreview, type MediaSpec } from "@/components/ui/MediaPreview/MediaPreview";
import { PlatformBadge, type PlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import { StatusPill, type DraftState } from "@/components/ui/StatusPill/StatusPill";
import styles from "./VariantCard.module.css";

export type WarnLevel = "warn" | "info" | "block" | "ok";

const WARN_ICON: Record<WarnLevel, IconName> = {
  warn: "alert",
  info: "info",
  block: "x-circle",
  ok: "check-circle",
};

export function ValidatorRow({ level = "warn", text }: { level?: WarnLevel; text: string }) {
  return (
    <div className={`${styles.validator} ${styles[`v_${level}`]}`} role="status">
      <Icon name={WARN_ICON[level]} size={13} />
      <span>{text}</span>
    </div>
  );
}

interface VariantCardProps {
  letter?: string;
  platform: PlatformKey;
  kind: string;
  status: DraftState;
  hook?: string;
  body: string;
  hashtags?: string[];
  warnings?: Array<{ level: WarnLevel; text: string }>;
  meta?: string;
  selected?: boolean;
  media?: MediaSpec;
  /** Footer action buttons (right-aligned). Flexible per surface. */
  actions?: ReactNode;
}

/**
 * The AI draft card. Two-column (media left, text right) when a media
 * spec is present, single-column otherwise. Matches library-distinct.jsx.
 */
export function VariantCard({
  letter,
  platform,
  kind,
  status,
  hook,
  body,
  hashtags,
  warnings = [],
  meta,
  selected,
  media,
  actions,
}: VariantCardProps) {
  const text = (
    <>
      {hook && <div className={styles.hook}>{hook}</div>}
      <div className={styles.body}>{body}</div>
      {hashtags && hashtags.length > 0 && (
        <div className={styles.hashtags}>
          {hashtags.map((h, i) => (
            <span key={i}>#{h.replace(/^#/, "")}</span>
          ))}
        </div>
      )}
    </>
  );

  return (
    <article className={`${styles.card} ${selected ? styles.selected : ""}`}>
      <header className={styles.head}>
        <div className={styles.headLeft}>
          {letter && <span className={styles.variantTag}>Variant {letter}</span>}
          <PlatformBadge platform={platform} size={16} />
          <span className={styles.kind}>{kind}</span>
        </div>
        <StatusPill state={status} size="sm" />
      </header>

      {media ? (
        <div
          className={styles.twoCol}
          style={{ gridTemplateColumns: media.type === "video" ? "180px 1fr" : "220px 1fr" }}
        >
          <MediaPreview {...media} platform={platform} inline />
          <div className={styles.textCol}>{text}</div>
        </div>
      ) : (
        <div className={styles.textCol}>{text}</div>
      )}

      {warnings.length > 0 && (
        <div className={styles.warnings}>
          {warnings.map((w, i) => (
            <ValidatorRow key={i} level={w.level} text={w.text} />
          ))}
        </div>
      )}

      {(meta || actions) && (
        <footer className={styles.foot}>
          <span className={styles.meta}>{meta}</span>
          {actions && <div className={styles.actions}>{actions}</div>}
        </footer>
      )}
    </article>
  );
}
